import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string | null;
  exchangeRate: string;
  isActive: boolean;
  isBase: boolean;
}

export default function CurrenciesSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    code: "", name: "", symbol: "",
    exchangeRate: "1.000000",
    isActive: true, isBase: false,
  });

  const { data: currencies = [] } = useQuery<Currency[]>({
    queryKey: ["/api/currencies", { includeInactive: "true" }],
    queryFn: () => api.get<Currency[]>(`/currencies?includeInactive=true`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/currencies/${editingId}`, form, sessionId)
      : api.post(`/currencies`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currencies"] });
      setDialogOpen(false);
      setEditingId(null);
      setForm({ code: "", name: "", symbol: "", exchangeRate: "1.000000", isActive: true, isBase: false });
      toast({ title: editingId ? "Currency updated" : "Currency created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/currencies/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currencies"] });
      toast({ title: "Currency deleted" });
    },
  });

  const handleEdit = (cur: Currency) => {
    setEditingId(cur.id);
    setForm({
      code: cur.code, name: cur.name, symbol: cur.symbol || "",
      exchangeRate: cur.exchangeRate, isActive: cur.isActive, isBase: cur.isBase,
    });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({ code: "", name: "", symbol: "", exchangeRate: "1.000000", isActive: true, isBase: false });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Currencies"
        description="Multi-currency configuration and exchange rates"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-currency">
              <Plus className="h-4 w-4 mr-1" /> New Currency
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="h-4 w-4" /> All Currencies ({currencies.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currencies.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No currencies configured.</p>
          ) : (
            <div className="space-y-2">
              {currencies.map((cur) => (
                <div
                  key={cur.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                  data-testid={`currency-row-${cur.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">{cur.code}</span>
                      {cur.symbol && <span className="text-muted-foreground">({cur.symbol})</span>}
                      <span className="text-sm">{cur.name}</span>
                      {cur.isBase && <Badge className="text-[10px]">Base</Badge>}
                      {!cur.isActive && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Exchange rate: {Number(cur.exchangeRate).toFixed(6)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(cur)}>Edit</Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteMutation.mutate(cur.id)}
                      disabled={cur.isBase}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Currency" : "New Currency"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Code *</label>
                <Input
                  value={form.code}
                  maxLength={3}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="USD"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Symbol</label>
                <Input
                  value={form.symbol}
                  onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                  placeholder="$"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="US Dollar"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Exchange Rate *</label>
              <Input
                type="number" step="0.000001" min={0}
                value={form.exchangeRate}
                onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">Rate against base currency</p>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isBase}
                  onChange={(e) => setForm({ ...form, isBase: e.target.checked })}
                />
                Base Currency
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.code || !form.name || saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
